// react
import { Fragment, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	Entity,
	EntityLoan,
	Form,
	FormName,
	Loan,
	Metadata,
	MetadataField,
	MetadataValue,
} from '../../../@types';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Grid, Typography, Paper } from '@mui/material';

// ui
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// utils
import { truncate } from '../../../utils/strings';

// theme
import { theme } from '../../../theme';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// props
export interface CardMetadataProps {
	loans: Loan[];
	entity: Entity;
}

export const CardMetadata = ({ loans, entity }: CardMetadataProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);

	// state
	const [isExpanded, setIsExpanded] = useState(false);
	const [form, setForm] = useState<Form | null>(null);

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	// hooks
	const { t } = useTranslation();

	// vars
	const associatedEntities: Entity[] = [];
	let formName: FormName = 'SUPPLIER';
	let metadataFields = entity.metadata;

	if (entity.type === 'SUPPLIER') {
		formName = 'SUPPLIER';
	} else if (entity.type === 'BENEFICIARY') {
		formName =
			entity.subtype === 'INDIVIDUAL' ? 'BENEFICIARY_INDIVIDUAL' : 'BENEFICIARY_ORGANIZATION';
	}
	// fetch form
	useFetch({
		isEnabled: Boolean(entity && project),
		onSuccess: (res) => {
			if (res.data) {
				setForm(res.data[0]);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},

		url: `${process.env.REACT_APP_API_URL}/forms?entity=${
			(entity?.loans[0] as EntityLoan)?.parent as number
		}&project=${
			project?.id
		}&name=${formName}&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
	});

	if (Array.isArray(metadataFields)) {
		// assign metadata value to form fields
		if (form) {
			metadataFields = (form?.fields as MetadataField[]).map((f) => ({
				...f,
				value: (metadataFields as MetadataField[]).find((m) => m.name === f.name)
					?.value as MetadataValue,
			}));
		}
	} else {
		// reformat existing metadata object into dynamic fields array
		const keys = Object.keys(metadataFields);

		const metadataFieldsConstructed: MetadataField[] = [];
		const metadataLegacyFields: Metadata = {
			address: 'Address',
			address1: 'Address 1',
			address2: 'Address 2',
			city: 'City',
			country: 'Country',
			postcode: 'Postcode',
			region: 'Region',
		};

		for (let i = 0; i < keys.length; i++) {
			metadataFieldsConstructed.push({
				name: keys[i],
				label: (metadataLegacyFields[keys[i]] as string) || keys[i],
				isProtected: true,
				value: metadataFields[keys[i]],
				type: 'TEXT',
			});
		}

		metadataFields = metadataFieldsConstructed;
	}

	// for each loan
	for (let i = 0; i < loans.length; i++) {
		const entityLoan = loans[i].entities.find((e) => e.id === entity.id);

		if (entityLoan && entityLoan.parent) {
			const entityExists = associatedEntities.find((e) => e.id === entityLoan.parent?.id);

			if (!entityExists) {
				associatedEntities.push(entityLoan.parent);
			}
		}
	}

	return (
		<Paper
			sx={(theme) => {
				return {
					position: 'relative',
					padding: theme.spacing(2),
					[theme.breakpoints.up('md')]: {
						padding: theme.spacing(3),
					},
					height: '100%',
				};
			}}>
			<Grid container spacing={3}>
				<Grid item xs={6} md={6} lg={4}>
					<Typography variant="h3">{t('associatedEntities')}</Typography>
					<Grid alignItems="center" container spacing={1}>
						{associatedEntities.length ? (
							<Grid item flex={1}>
								{associatedEntities.map((entityParent, i) => {
									const isLast = i === associatedEntities.length - 1;
									return (
										<Fragment key={`entity-${i}`}>
											<LinkEntity
												entity={{
													...entityParent,
													name: truncate({ end: 20, str: entityParent.name, suffix: '...' }),
												}}
											/>
											{!isLast ? ', ' : ''}
										</Fragment>
									);
								})}
							</Grid>
						) : (
							<Grid item>
								<Typography>{t('na')}</Typography>
							</Grid>
						)}
					</Grid>
				</Grid>
				<Grid item xs={6} md={6} lg={4}>
					<Typography variant="h3">{t('uniqueIdentifier')}</Typography>
					<Typography>{entity?.idExternal ? entity?.idExternal : t('na')}</Typography>
				</Grid>
				<Grid item xs={6} md={6} lg={3}>
					<Typography variant="h3">{t('wallet')}</Typography>
					<BlockchainHash hash={entity.wallet?.address} />
				</Grid>
				{isExpanded && (
					<>
						{metadataFields.map((field, i) => {
							return (
								<Grid item key={`field-${field.name}-${i}`} xs={6} md={6} lg={4}>
									<Typography variant="h3">{t(`${field.label}`)}</Typography>
									{field.name === 'country' ? (
										<Typography>
											{field?.value
												? new Intl.DisplayNames(['en'], { type: 'region' }).of(String(field.value))
												: t('na')}
										</Typography>
									) : (
										<Typography> {field?.value ? String(field.value) : t('na')}</Typography>
									)}
								</Grid>
							);
						})}
					</>
				)}
			</Grid>
			<BtnExpand
				hasDisplayToggled={false}
				isExpanded={isExpanded}
				label={isExpanded ? t('hideDetails') : t('seeDetails')}
				onClick={handleToggleCard}
				sxIconButton={{
					position: 'absolute',
					top: theme.spacing(1),
					right: theme.spacing(1),
				}}
			/>
		</Paper>
	);
};
